import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (token, user) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setToken(token);
    setUser(user);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Settings Context
const SettingsContext = createContext(null);

const SettingsProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => localStorage.getItem('dairypilot_currency') || 'INR');
  const [metricSystem, setMetricSystem] = useState(() => localStorage.getItem('dairypilot_metric') || 'metric');

  const currencySymbols = {
    INR: '₹',
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    AUD: 'A$',
    CAD: 'C$'
  };

  const getCurrencySymbol = () => currencySymbols[currency] || '₹';

  const formatCurrency = (amount) => {
    return `${getCurrencySymbol()}${amount.toLocaleString()}`;
  };

  const getVolumeUnit = () => metricSystem === 'imperial' ? 'Gallons' : 'Liters';
  const getWeightUnit = () => metricSystem === 'imperial' ? 'Lbs' : 'Kg';

  const updateSettings = (newCurrency, newMetricSystem) => {
    setCurrency(newCurrency);
    setMetricSystem(newMetricSystem);
    localStorage.setItem('dairypilot_currency', newCurrency);
    localStorage.setItem('dairypilot_metric', newMetricSystem);
  };

  return (
    <SettingsContext.Provider value={{
      currency,
      metricSystem,
      getCurrencySymbol,
      formatCurrency,
      getVolumeUnit,
      getWeightUnit,
      updateSettings
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

const useSettings = () => useContext(SettingsContext);

// Auth Page
const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const data = isLogin ? { email, password } : { email, password, name };
      const response = await axios.post(`${API}${endpoint}`, data);
      login(response.data.access_token, response.data.user);
      toast.success(isLogin ? "Login successful!" : "Registration successful!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await axios.post(`${API}/auth/google`, {
        credential: credentialResponse.credential
      });
      login(response.data.access_token, response.data.user);
      toast.success("Google login successful!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Google authentication failed");
    }
  };

  const handleGoogleError = () => {
    toast.error("Google login failed");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <Card className="w-full max-w-md mx-4 shadow-xl border-green-100">
        <CardHeader className="space-y-1 text-center">
          <img src="/logo.png?v=2" alt="DairyPilot Logo" className="mx-auto w-16 h-16 mb-4 rounded-2xl shadow-lg" />
          <CardTitle className="text-3xl font-bold text-green-800">DairyPilot</CardTitle>
          <CardDescription className="text-base">Manage your dairy farm efficiently</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={isLogin ? "login" : "register"} onValueChange={(v) => setIsLogin(v === "login")} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" data-testid="login-button">
                  Login
                </Button>
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-gray-500">Or continue with</span>
                  </div>
                </div>

                <div className="w-full flex justify-center">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="signin_with"
                    size="large"
                    width="100%"
                  />
                </div>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email">Email</Label>
                  <Input id="reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Password</Label>
                  <Input id="reg-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" data-testid="register-button">
                  Register
                </Button>
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-gray-500">Or continue with</span>
                  </div>
                </div>

                <div className="w-full flex justify-center">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="signup_with"
                    size="large"
                    width="100%"
                  />
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Page
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const { user, logout } = useAuth();
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    newPassword: '',
    confirmPassword: ''
  });
  const { currency, metricSystem, updateSettings } = useSettings();
  const [tempCurrency, setTempCurrency] = useState(currency);
  const [tempMetricSystem, setTempMetricSystem] = useState(metricSystem);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    setTempCurrency(currency);
    setTempMetricSystem(metricSystem);
  }, [currency, metricSystem]);

  const handleSaveSettings = () => {
    updateSettings(tempCurrency, tempMetricSystem);
    toast.success("Settings saved successfully!");
    setShowSettings(false);
  };

  const handleUpdateProfile = async () => {
    // Validate passwords if trying to change password
    if (profileForm.newPassword) {
      if (profileForm.newPassword !== profileForm.confirmPassword) {
        toast.error("New passwords do not match");
        return;
      }
      if (profileForm.newPassword.length < 8) {
        toast.error("Password must be at least 8 characters long");
        return;
      }
      // Check for at least one capital letter
      if (!/[A-Z]/.test(profileForm.newPassword)) {
        toast.error("Password must contain at least one capital letter");
        return;
      }
      // Check for at least one number
      if (!/[0-9]/.test(profileForm.newPassword)) {
        toast.error("Password must contain at least one number");
        return;
      }
    }

    try {
      const updateData = { name: profileForm.name };
      if (profileForm.newPassword) {
        updateData.newPassword = profileForm.newPassword;
      }

      await axios.put(`${API}/users/profile`, updateData);
      
      // Update local storage with new name
      const updatedUser = { ...user, name: profileForm.name };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
      toast.success("Profile updated successfully!");
      setShowProfile(false);
      setProfileForm({
        name: profileForm.name,
        newPassword: '',
        confirmPassword: ''
      });
      
      // Refresh the page to show updated name
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to fetch dashboard stats");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <nav className="glass-effect backdrop-blur-xl border-b border-white/30 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center gap-3">
              <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-12 h-12 rounded-xl shadow-lg transform hover:scale-110 transition-transform duration-300" />
              <span className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">DairyPilot</span>
            </div>
            <div className="flex items-center gap-6">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-4 py-2 bg-white/60 rounded-full hover:bg-white/80 transition-all cursor-pointer">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-semibold text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-gray-700">Welcome, {user?.name}</span>
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowProfile(true)} className="cursor-pointer">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowSettings(true)} className="cursor-pointer">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Profile Dialog */}
      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-teal-700">Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <Label className="text-base font-semibold mb-2 block">Name</Label>
              <Input
                type="text"
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                className="h-12 text-base border-2 border-teal-200 focus:border-teal-500"
                placeholder="Enter your name"
              />
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Change Password (Optional)</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold mb-2 block">New Password</Label>
                  <Input
                    type="password"
                    value={profileForm.newPassword}
                    onChange={(e) => setProfileForm({ ...profileForm, newPassword: e.target.value })}
                    className="h-10 text-base border-2 border-gray-200"
                    placeholder="Min 8 chars, 1 capital letter, 1 number"
                  />
                </div>

                <div>
                  <Label className="text-sm font-semibold mb-2 block">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={profileForm.confirmPassword}
                    onChange={(e) => setProfileForm({ ...profileForm, confirmPassword: e.target.value })}
                    className="h-10 text-base border-2 border-gray-200"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleUpdateProfile} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-12 text-base font-semibold">
                Update Profile
              </Button>
              <Button onClick={() => setShowProfile(false)} variant="outline" className="flex-1 h-12 text-base border-2 border-gray-300">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-teal-700">Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <Label className="text-base font-semibold mb-3 block">Currency</Label>
              <Select value={tempCurrency} onValueChange={setTempCurrency}>
                <SelectTrigger className="h-12 text-base border-2 border-teal-200 focus:border-teal-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">₹ INR - Indian Rupee</SelectItem>
                  <SelectItem value="USD">$ USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">€ EUR - Euro</SelectItem>
                  <SelectItem value="GBP">£ GBP - British Pound</SelectItem>
                  <SelectItem value="JPY">¥ JPY - Japanese Yen</SelectItem>
                  <SelectItem value="AUD">A$ AUD - Australian Dollar</SelectItem>
                  <SelectItem value="CAD">C$ CAD - Canadian Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-base font-semibold mb-3 block">Metric System</Label>
              <Select value={tempMetricSystem} onValueChange={setTempMetricSystem}>
                <SelectTrigger className="h-12 text-base border-2 border-teal-200 focus:border-teal-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="metric">Metric (Liters, Kilograms, Celsius)</SelectItem>
                  <SelectItem value="imperial">Imperial (Gallons, Pounds, Fahrenheit)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleSaveSettings} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-12 text-base font-semibold">
                Save Settings
              </Button>
              <Button onClick={() => setShowSettings(false)} variant="outline" className="flex-1 h-12 text-base border-2 border-gray-300">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12 text-center">
          <h1 className="text-6xl font-extrabold mb-4">
            <span className="bg-gradient-to-r from-teal-600 via-emerald-500 to-green-600 bg-clip-text text-transparent">
              Welcome Home
            </span>
          </h1>
          <p className="text-xl text-gray-600 font-light max-w-2xl mx-auto">Choose your destination to manage your dairy farm efficiently</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-6xl mx-auto">
          {/* Dashboard Overview Tile */}
          <Link to="/overview" className="block group">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 h-96 cursor-pointer transform hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-cyan-500 to-teal-600"></div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30"></div>
              <div className="relative h-full flex flex-col items-center justify-center text-white p-8">
                <div className="mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                    <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
                <h2 className="text-5xl font-bold mb-4 tracking-tight">Dashboard Overview</h2>
                <p className="text-2xl text-white/90 font-light">View key metrics & statistics</p>
                <div className="mt-6 flex items-center gap-2 text-lg font-medium group-hover:gap-4 transition-all">
                  <span>Explore Now</span>
                  <svg className="w-6 h-6 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            </div>
          </Link>

          {/* Track & Manage Tile */}
          <Link to="/tracking" className="block group">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 h-96 cursor-pointer transform hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-green-500 to-teal-600"></div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZG90cyIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48Y2lyY2xlIGN4PSIyIiBjeT0iMiIgcj0iMSIgZmlsbD0id2hpdGUiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2RvdHMpIi8+PC9zdmc+')] opacity-40"></div>
              <div className="relative h-full flex flex-col items-center justify-center text-white p-8">
                <div className="mb-6 transform group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500">
                  <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                    <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                </div>
                <h2 className="text-5xl font-bold mb-4 tracking-tight">Track & Manage</h2>
                <p className="text-2xl text-white/90 font-light">Add and track farm activities</p>
                <div className="mt-6 flex items-center gap-2 text-lg font-medium group-hover:gap-4 transition-all">
                  <span>Get Started</span>
                  <svg className="w-6 h-6 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Overview Page - Dashboard Details
const OverviewPage = () => {
  const [stats, setStats] = useState(null);
  const { user, logout } = useAuth();
  const { formatCurrency } = useSettings();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to fetch dashboard stats");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <nav className="bg-white border-b border-green-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-10 h-10 mr-3 rounded-lg" />
                <span className="text-2xl font-bold text-green-800">DairyPilot</span>
              </Link>
              <Link to="/" className="text-gray-600 hover:text-green-600">Home</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-green-900 mb-2">Dashboard Overview</h1>
          <p className="text-gray-600">Key metrics and statistics for your farm</p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-white border-green-100 shadow-md hover:shadow-lg transition-all">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">Total Animals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-700">{stats.total_animals}</div>
              </CardContent>
            </Card>

            <Card className="bg-white border-green-100 shadow-md hover:shadow-lg transition-all">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">Total Products</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-700">{stats.total_products}</div>
              </CardContent>
            </Card>

            <Link to="/income-analytics">
              <Card className="bg-white border-green-100 shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                    Monthly Income
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-700">{formatCurrency(stats.current_month_income)}</div>
                  <div className="mt-2 text-xs text-green-600 font-medium flex items-center gap-1">
                    <span>View Analytics</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/expense-analytics">
              <Card className="bg-white border-red-100 shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                    Monthly Expenses
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                    </svg>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-red-600">{formatCurrency(stats.current_month_expenses)}</div>
                  <div className="mt-2 text-xs text-red-600 font-medium flex items-center gap-1">
                    <span>View Analytics</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/profit-analytics">
              <Card className={`bg-white shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer ${stats.current_month_profit >= 0 ? 'border-green-100' : 'border-red-100'}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                    Monthly Profit
                    <svg className={`w-5 h-5 ${stats.current_month_profit >= 0 ? 'text-green-500' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${stats.current_month_profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    {formatCurrency(stats.current_month_profit)}
                  </div>
                  <div className={`mt-2 text-xs font-medium flex items-center gap-1 ${stats.current_month_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <span>View Analytics</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/production-analytics">
              <Card className="bg-white border-green-100 shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                    Monthly Production
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.production_by_product && Object.keys(stats.production_by_product).length > 0 ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-4">
                        {Object.entries(stats.production_by_product).map(([product, quantity]) => (
                          <div key={product} className="flex flex-col">
                            <span className="text-xs text-gray-500 uppercase">{product}</span>
                            <span className="text-2xl font-bold text-blue-700">{quantity.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <span className="text-xs text-gray-500">Total: </span>
                        <span className="text-lg font-semibold text-blue-600">{stats.current_month_production.toFixed(2)} units</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-3xl font-bold text-blue-700">{stats.current_month_production.toFixed(2)} units</div>
                  )}
                  <div className="mt-3 text-xs text-blue-600 font-medium flex items-center gap-1">
                    <span>View Trends</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        )}

      </div>
    </div>
  );
};

// Income Analytics Page
const IncomeAnalyticsPage = () => {
  const [incomeData, setIncomeData] = useState({ pie_data: [], time_data: [], categories: [] });
  const [timeUnit, setTimeUnit] = useState("month");
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedPiePeriod, setSelectedPiePeriod] = useState("");
  const [loading, setLoading] = useState(false);
  const { getCurrencySymbol } = useSettings();
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  useEffect(() => {
    fetchData();
  }, [timeUnit]);

  useEffect(() => {
    if (incomeData.time_data.length > 0) {
      const periods = incomeData.time_data.map(item => item.period);
      setAvailablePeriods(periods);
      if (!selectedPiePeriod || !periods.includes(selectedPiePeriod)) {
        setSelectedPiePeriod(periods[periods.length - 1]); // Default to latest period
      }
    }
  }, [incomeData.time_data]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/income/category-breakdown?time_unit=${timeUnit}`);
      setIncomeData(res.data);
    } catch (error) {
      toast.error("Failed to fetch income data");
    } finally {
      setLoading(false);
    }
  };

  const getPieDataForPeriod = () => {
    if (!selectedPiePeriod) return incomeData.pie_data;
    
    const periodData = incomeData.time_data.find(item => item.period === selectedPiePeriod);
    if (!periodData) return incomeData.pie_data;

    return incomeData.categories.map(cat => ({
      name: cat,
      value: periodData[cat] || 0
    })).filter(item => item.value > 0);
  };

  const renderPieLabel = (entry) => {
    const pieData = getPieDataForPeriod();
    const total = pieData.reduce((sum, item) => sum + item.value, 0);
    const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : 0;
    return `${entry.name}: ${formatAmount(entry.value)} (${percentage}%)`;
  };

  const getTimeLabel = () => ({ month: "Monthly", quarter: "Quarterly", year: "Yearly" }[timeUnit] || "Monthly");
  
  const formatAmount = (value) => `${getCurrencySymbol()}${value.toLocaleString()}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <nav className="glass-effect backdrop-blur-xl border-b border-white/30 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-3">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-12 h-12 rounded-xl shadow-lg transform hover:scale-110 transition-transform duration-300" />
                <span className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">DairyPilot</span>
              </Link>
              <Link to="/overview" className="text-gray-600 hover:text-teal-600 font-medium">Overview</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-5xl font-bold mb-3">
            <span className="bg-gradient-to-r from-green-600 via-emerald-500 to-teal-600 bg-clip-text text-transparent">
              Income Analytics
            </span>
          </h1>
          <p className="text-xl text-gray-600">Analyze income by sources and trends</p>
        </div>

        <Card className="glass-effect mb-6">
          <CardContent className="p-6">
            <div className="max-w-md">
              <Label className="text-base font-semibold mb-3 block">Time Period</Label>
              <Select value={timeUnit} onValueChange={setTimeUnit}>
                <SelectTrigger className="h-12 text-base border-2 border-green-200 focus:border-green-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monthly</SelectItem>
                  <SelectItem value="quarter">Quarterly</SelectItem>
                  <SelectItem value="year">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <Card className="glass-effect"><CardContent className="p-12 text-center"><div className="animate-spin w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div><p className="text-gray-600">Loading...</p></CardContent></Card>
        ) : incomeData.pie_data.length > 0 ? (
          <div className="space-y-6">
            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">Income Distribution by Source</CardTitle>
                  <div className="w-48">
                    {availablePeriods.length > 0 ? (
                      <Select value={selectedPiePeriod} onValueChange={setSelectedPiePeriod}>
                        <SelectTrigger className="bg-white text-gray-800 border-0 h-10">
                          <SelectValue placeholder="Select period" />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePeriods.map((period) => (
                            <SelectItem key={period} value={period}>{period}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-xs text-white/80 text-center py-2">Period selector will appear with data</div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie data={getPieDataForPeriod()} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} label={renderPieLabel}>
                      {getPieDataForPeriod().map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                    </Pie>
                    <Tooltip formatter={(value) => formatAmount(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                <CardTitle className="text-xl">{getTimeLabel()} Income Comparison</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={incomeData.time_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                    <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #10b981', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                    <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                    {incomeData.categories.map((cat, idx) => (<Bar key={cat} dataKey={cat} fill={COLORS[idx % COLORS.length]} name={cat} radius={[8, 8, 0, 0]} />))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                <CardTitle className="text-xl">{getTimeLabel()} Income Trends</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={incomeData.time_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                    <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #10b981', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                    <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                    {incomeData.categories.map((cat, idx) => (<Line key={cat} type="monotone" dataKey={cat} stroke={COLORS[idx % COLORS.length]} strokeWidth={2} name={cat} dot={{ r: 4 }} />))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="glass-effect"><CardContent className="p-12 text-center"><p className="text-gray-500">No income data available</p></CardContent></Card>
        )}
      </div>
    </div>
  );
};

// Expense Analytics Page
const ExpenseAnalyticsPage = () => {
  const [expenseData, setExpenseData] = useState({ pie_data: [], time_data: [], categories: [] });
  const [timeUnit, setTimeUnit] = useState("month");
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedPiePeriod, setSelectedPiePeriod] = useState("");
  const [loading, setLoading] = useState(false);
  const { getCurrencySymbol } = useSettings();
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  useEffect(() => {
    fetchData();
  }, [timeUnit]);

  useEffect(() => {
    if (expenseData.time_data.length > 0) {
      const periods = expenseData.time_data.map(item => item.period);
      setAvailablePeriods(periods);
      if (!selectedPiePeriod || !periods.includes(selectedPiePeriod)) {
        setSelectedPiePeriod(periods[periods.length - 1]); // Default to latest period
      }
    }
  }, [expenseData.time_data]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/expenses/category-breakdown?time_unit=${timeUnit}`);
      setExpenseData(res.data);
    } catch (error) {
      toast.error("Failed to fetch expense data");
    } finally {
      setLoading(false);
    }
  };

  const getPieDataForPeriod = () => {
    if (!selectedPiePeriod) return expenseData.pie_data;
    
    const periodData = expenseData.time_data.find(item => item.period === selectedPiePeriod);
    if (!periodData) return expenseData.pie_data;

    return expenseData.categories.map(cat => ({
      name: cat,
      value: periodData[cat] || 0
    })).filter(item => item.value > 0);
  };

  const renderPieLabel = (entry) => {
    const pieData = getPieDataForPeriod();
    const total = pieData.reduce((sum, item) => sum + item.value, 0);
    const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : 0;
    return `${entry.name}: ${formatAmount(entry.value)} (${percentage}%)`;
  };

  const getTimeLabel = () => ({ month: "Monthly", quarter: "Quarterly", year: "Yearly" }[timeUnit] || "Monthly");
  
  const formatAmount = (value) => `${getCurrencySymbol()}${value.toLocaleString()}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <nav className="glass-effect backdrop-blur-xl border-b border-white/30 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-3">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-12 h-12 rounded-xl shadow-lg transform hover:scale-110 transition-transform duration-300" />
                <span className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">DairyPilot</span>
              </Link>
              <Link to="/overview" className="text-gray-600 hover:text-teal-600 font-medium">Overview</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-5xl font-bold mb-3">
            <span className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-600 bg-clip-text text-transparent">
              Expense Analytics
            </span>
          </h1>
          <p className="text-xl text-gray-600">Analyze expenses by categories and trends</p>
        </div>

        <Card className="glass-effect mb-6">
          <CardContent className="p-6">
            <div className="max-w-md">
              <Label className="text-base font-semibold mb-3 block">Time Period</Label>
              <Select value={timeUnit} onValueChange={setTimeUnit}>
                <SelectTrigger className="h-12 text-base border-2 border-red-200 focus:border-red-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monthly</SelectItem>
                  <SelectItem value="quarter">Quarterly</SelectItem>
                  <SelectItem value="year">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <Card className="glass-effect"><CardContent className="p-12 text-center"><div className="animate-spin w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div><p className="text-gray-600">Loading...</p></CardContent></Card>
        ) : expenseData.pie_data.length > 0 ? (
          <div className="space-y-6">
            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-red-500 to-orange-600 text-white">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">Expense Distribution by Category</CardTitle>
                  <div className="w-48">
                    {availablePeriods.length > 0 ? (
                      <Select value={selectedPiePeriod} onValueChange={setSelectedPiePeriod}>
                        <SelectTrigger className="bg-white text-gray-800 border-0 h-10">
                          <SelectValue placeholder="Select period" />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePeriods.map((period) => (
                            <SelectItem key={period} value={period}>{period}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-xs text-white/80 text-center py-2">Period selector will appear with data</div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie data={getPieDataForPeriod()} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} label={renderPieLabel}>
                      {getPieDataForPeriod().map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                    </Pie>
                    <Tooltip formatter={(value) => formatAmount(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-red-500 to-orange-600 text-white">
                <CardTitle className="text-xl">{getTimeLabel()} Expense Comparison</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={expenseData.time_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                    <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #ef4444', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                    <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                    {expenseData.categories.map((cat, idx) => (<Bar key={cat} dataKey={cat} fill={COLORS[idx % COLORS.length]} name={cat} radius={[8, 8, 0, 0]} />))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-red-500 to-orange-600 text-white">
                <CardTitle className="text-xl">{getTimeLabel()} Expense Trends</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={expenseData.time_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                    <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #ef4444', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                    <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                    {expenseData.categories.map((cat, idx) => (<Line key={cat} type="monotone" dataKey={cat} stroke={COLORS[idx % COLORS.length]} strokeWidth={2} name={cat} dot={{ r: 4 }} />))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="glass-effect"><CardContent className="p-12 text-center"><p className="text-gray-500">No expense data available</p></CardContent></Card>
        )}
      </div>
    </div>
  );
};

// Profit Analytics Page
const ProfitAnalyticsPage = () => {
  const [profitData, setProfitData] = useState([]);
  const [timeUnit, setTimeUnit] = useState("month");
  const [loading, setLoading] = useState(false);
  const { getCurrencySymbol } = useSettings();

  useEffect(() => {
    fetchData();
  }, [timeUnit]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/financial/analytics?time_unit=${timeUnit}`);
      setProfitData(res.data.data);
    } catch (error) {
      toast.error("Failed to fetch profit data");
    } finally {
      setLoading(false);
    }
  };

  const getTimeLabel = () => ({ month: "Monthly", quarter: "Quarterly", year: "Yearly" }[timeUnit] || "Monthly");
  
  const formatAmount = (value) => `${getCurrencySymbol()}${value.toLocaleString()}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      <nav className="glass-effect backdrop-blur-xl border-b border-white/30 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-3">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-12 h-12 rounded-xl shadow-lg transform hover:scale-110 transition-transform duration-300" />
                <span className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">DairyPilot</span>
              </Link>
              <Link to="/overview" className="text-gray-600 hover:text-teal-600 font-medium">Overview</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-5xl font-bold mb-3">
            <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-600 bg-clip-text text-transparent">
              Profit Analytics
            </span>
          </h1>
          <p className="text-xl text-gray-600">Analyze profit trends over time</p>
        </div>

        <Card className="glass-effect mb-6">
          <CardContent className="p-6">
            <div className="max-w-md">
              <Label className="text-base font-semibold mb-3 block">Time Period</Label>
              <Select value={timeUnit} onValueChange={setTimeUnit}>
                <SelectTrigger className="h-12 text-base border-2 border-blue-200 focus:border-blue-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monthly</SelectItem>
                  <SelectItem value="quarter">Quarterly</SelectItem>
                  <SelectItem value="year">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <Card className="glass-effect"><CardContent className="p-12 text-center"><div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div><p className="text-gray-600">Loading...</p></CardContent></Card>
        ) : profitData.length > 0 ? (
          <div className="space-y-6">
            {/* Line Chart - Income, Expenses & Profit Trend */}
            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white">
                <CardTitle className="text-2xl">{getTimeLabel()} Financial Trends</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={profitData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                    <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #3b82f6', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                    <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                    <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={3} name="Income" dot={{ r: 6, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={3} name="Expenses" dot={{ r: 6, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={3} name="Profit" dot={{ r: 6, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 8 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Bar Chart - Income, Expenses, Profit Comparison */}
            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                <CardTitle className="text-2xl">{getTimeLabel()} Income vs Expenses vs Profit</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={profitData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                    <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #10b981', borderRadius: '12px', padding: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }} formatter={(value) => formatAmount(value)} />
                    <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                    <Bar dataKey="income" fill="#10b981" name="Income" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="profit" fill="#3b82f6" name="Profit" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Bar Chart - Profit Only */}
            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white">
                <CardTitle className="text-2xl">{getTimeLabel()} Profit Bars</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={profitData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                    <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #3b82f6', borderRadius: '12px', padding: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }} formatter={(value) => formatAmount(value)} />
                    <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                    <Bar dataKey="profit" fill="#3b82f6" name="Profit" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="glass-effect"><CardContent className="p-12 text-center"><p className="text-gray-500">No profit data available</p></CardContent></Card>
        )}
      </div>
    </div>
  );
};

// Tracking Page - All Management Modules
const TrackingPage = () => {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <nav className="bg-white border-b border-green-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-10 h-10 mr-3 rounded-lg" />
                <span className="text-2xl font-bold text-green-800">DairyPilot</span>
              </Link>
              <Link to="/" className="text-gray-600 hover:text-green-600">Home</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-green-900 mb-2">Track & Manage</h1>
          <p className="text-gray-600">Add and track all your farm activities</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Link to="/financial" className="block">
            <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white hover:shadow-xl transition-all cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Financial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-green-100">Manage expenses and income</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/production" className="block">
            <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white hover:shadow-xl transition-all cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Production
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-100">Track daily production</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/animals" className="block">
            <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white hover:shadow-xl transition-all cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Animals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-amber-100">Manage your livestock</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/products" className="block">
            <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white hover:shadow-xl transition-all cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  Products
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-purple-100">Manage product catalog</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Production Analytics Page
const ProductionAnalyticsPage = () => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [timeUnit, setTimeUnit] = useState("day");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedProduct, timeUnit]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setAllProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        time_unit: timeUnit
      });
      if (selectedProduct !== "all") {
        params.append("product_id", selectedProduct);
      }
      const response = await axios.get(`${API}/production/analytics?${params}`);
      setAnalyticsData(response.data.data);
      setProducts(response.data.products);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  const getTimeLabel = () => {
    const labels = {
      day: "Daily",
      week: "Weekly",
      month: "Monthly",
      quarter: "Quarterly",
      year: "Yearly"
    };
    return labels[timeUnit] || "Daily";
  };

  const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <nav className="glass-effect backdrop-blur-xl border-b border-white/30 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-3">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-12 h-12 rounded-xl shadow-lg transform hover:scale-110 transition-transform duration-300" />
                <span className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">DairyPilot</span>
              </Link>
              <Link to="/overview" className="text-gray-600 hover:text-teal-600 font-medium">Overview</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-5xl font-bold mb-3">
            <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-600 bg-clip-text text-transparent">
              Production Analytics
            </span>
          </h1>
          <p className="text-xl text-gray-600">Track and analyze production trends over time</p>
        </div>

        {/* Filters */}
        <Card className="mb-8 glass-effect">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-base font-semibold mb-3 block">Select Product</Label>
                <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                  <SelectTrigger className="h-12 text-base border-2 border-teal-200 focus:border-teal-500">
                    <SelectValue placeholder="Choose a product" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {allProducts.map((product) => (
                      <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">Time Period</Label>
                <Select value={timeUnit} onValueChange={setTimeUnit}>
                  <SelectTrigger className="h-12 text-base border-2 border-teal-200 focus:border-teal-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Daily</SelectItem>
                    <SelectItem value="week">Weekly</SelectItem>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="quarter">Quarterly</SelectItem>
                    <SelectItem value="year">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charts */}
        {loading ? (
          <Card className="glass-effect">
            <CardContent className="p-12 text-center">
              <div className="animate-spin w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading analytics...</p>
            </CardContent>
          </Card>
        ) : analyticsData.length > 0 ? (
          <div className="space-y-8">
            {/* Line Chart */}
            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                  {getTimeLabel()} Production Trends
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={analyticsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="period" 
                      stroke="#666"
                      style={{ fontSize: '12px', fontWeight: '500' }}
                    />
                    <YAxis 
                      stroke="#666"
                      style={{ fontSize: '12px', fontWeight: '500' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '2px solid #14b8a6',
                        borderRadius: '12px',
                        padding: '12px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }}
                    />
                    {products.map((product, index) => (
                      <Line
                        key={product}
                        type="monotone"
                        dataKey={product}
                        stroke={colors[index % colors.length]}
                        strokeWidth={3}
                        dot={{ r: 5, strokeWidth: 2, fill: '#fff' }}
                        activeDot={{ r: 7, strokeWidth: 2 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Bar Chart */}
            <Card className="glass-effect overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  {getTimeLabel()} Production Comparison
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={analyticsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="period" 
                      stroke="#666"
                      style={{ fontSize: '12px', fontWeight: '500' }}
                    />
                    <YAxis 
                      stroke="#666"
                      style={{ fontSize: '12px', fontWeight: '500' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '2px solid #10b981',
                        borderRadius: '12px',
                        padding: '12px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }}
                    />
                    {products.map((product, index) => (
                      <Bar
                        key={product}
                        dataKey={product}
                        fill={colors[index % colors.length]}
                        radius={[8, 8, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="glass-effect">
            <CardContent className="p-12 text-center">
              <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data Available</h3>
              <p className="text-gray-600">Start tracking production to see analytics and trends.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Financial Analytics Page
const FinancialAnalyticsPage = () => {
  const [profitData, setProfitData] = useState([]);
  const [expenseData, setExpenseData] = useState({ pie_data: [], time_data: [], categories: [] });
  const [incomeData, setIncomeData] = useState({ pie_data: [], time_data: [], categories: [] });
  const [timeUnit, setTimeUnit] = useState("month");
  const [loading, setLoading] = useState(false);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  useEffect(() => {
    fetchAllAnalytics();
  }, [timeUnit]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const [profitRes, expenseRes, incomeRes] = await Promise.all([
        axios.get(`${API}/financial/analytics?time_unit=${timeUnit}`),
        axios.get(`${API}/expenses/category-breakdown?time_unit=${timeUnit}`),
        axios.get(`${API}/income/category-breakdown?time_unit=${timeUnit}`)
      ]);
      setProfitData(profitRes.data.data);
      setExpenseData(expenseRes.data);
      setIncomeData(incomeRes.data);
    } catch (error) {
      toast.error("Failed to fetch financial analytics");
    } finally {
      setLoading(false);
    }
  };

  const getTimeLabel = () => {
    const labels = {
      month: "Monthly",
      quarter: "Quarterly",
      year: "Yearly"
    };
    return labels[timeUnit] || "Monthly";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <nav className="glass-effect backdrop-blur-xl border-b border-white/30 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-3">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-12 h-12 rounded-xl shadow-lg transform hover:scale-110 transition-transform duration-300" />
                <span className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">DairyPilot</span>
              </Link>
              <Link to="/overview" className="text-gray-600 hover:text-teal-600 font-medium">Overview</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-5xl font-bold mb-3">
            <span className="bg-gradient-to-r from-green-600 via-emerald-500 to-teal-600 bg-clip-text text-transparent">
              Financial Analytics
            </span>
          </h1>
          <p className="text-xl text-gray-600">Track profit trends and analyze expense & income category breakdowns</p>
        </div>

        {/* Filter */}
        <Card className="mb-8 glass-effect">
          <CardContent className="p-6">
            <div className="max-w-md">
              <Label className="text-base font-semibold mb-3 block">Time Period</Label>
              <Select value={timeUnit} onValueChange={setTimeUnit}>
                <SelectTrigger className="h-12 text-base border-2 border-teal-200 focus:border-teal-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monthly</SelectItem>
                  <SelectItem value="quarter">Quarterly</SelectItem>
                  <SelectItem value="year">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Charts */}
        {loading ? (
          <Card className="glass-effect">
            <CardContent className="p-12 text-center">
              <div className="animate-spin w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading analytics...</p>
            </CardContent>
          </Card>
        ) : profitData.length > 0 ? (
          <div className="space-y-8">
            {/* PROFIT TRENDS - ONLY PROFIT */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-gray-800">Profit Trends</h2>
              <Card className="glass-effect overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white">
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {getTimeLabel()} Profit Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={profitData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                      <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #3b82f6', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                      <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                      <Line type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={3} name="Profit" dot={{ r: 6, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 8 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Expense Analysis */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-gray-800">Expense Breakdown</h2>
              
              {/* Expense Pie Chart */}
              <Card className="glass-effect overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-red-500 to-orange-600 text-white">
                  <CardTitle className="text-xl">Expense Distribution by Category</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie data={expenseData.pie_data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} label={(entry) => `${entry.name}: ${formatAmount(entry.value)}`}>
                        {expenseData.pie_data.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatAmount(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Expense Bar Chart */}
              <Card className="glass-effect overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-red-500 to-orange-600 text-white">
                  <CardTitle className="text-xl">{getTimeLabel()} Expense Comparison by Category</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={expenseData.time_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                      <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #ef4444', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                      <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                      {expenseData.categories.map((cat, idx) => (
                        <Bar key={cat} dataKey={cat} fill={COLORS[idx % COLORS.length]} name={cat} radius={[8, 8, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Expense Line Chart */}
              <Card className="glass-effect overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-red-500 to-orange-600 text-white">
                  <CardTitle className="text-xl">{getTimeLabel()} Expense Trends by Category</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={expenseData.time_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                      <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #ef4444', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                      <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                      {expenseData.categories.map((cat, idx) => (
                        <Line key={cat} type="monotone" dataKey={cat} stroke={COLORS[idx % COLORS.length]} strokeWidth={2} name={cat} dot={{ r: 4 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Income Analysis */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-gray-800">Income Breakdown</h2>
              
              {/* Income Pie Chart */}
              <Card className="glass-effect overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                  <CardTitle className="text-xl">Income Distribution by Source</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie data={incomeData.pie_data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} label={(entry) => `${entry.name}: ${formatAmount(entry.value)}`}>
                        {incomeData.pie_data.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatAmount(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Income Bar Chart */}
              <Card className="glass-effect overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                  <CardTitle className="text-xl">{getTimeLabel()} Income Comparison by Source</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={incomeData.time_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                      <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #10b981', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                      <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                      {incomeData.categories.map((cat, idx) => (
                        <Bar key={cat} dataKey={cat} fill={COLORS[idx % COLORS.length]} name={cat} radius={[8, 8, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Income Line Chart */}
              <Card className="glass-effect overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                  <CardTitle className="text-xl">{getTimeLabel()} Income Trends by Source</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={incomeData.time_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="period" stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} />
                      <YAxis stroke="#666" style={{ fontSize: '13px', fontWeight: '500' }} tickFormatter={(value) => formatAmount(value)} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '2px solid #10b981', borderRadius: '12px', padding: '12px' }} formatter={(value) => formatAmount(value)} />
                      <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '14px', fontWeight: '600' }} />
                      {incomeData.categories.map((cat, idx) => (
                        <Line key={cat} type="monotone" dataKey={cat} stroke={COLORS[idx % COLORS.length]} strokeWidth={2} name={cat} dot={{ r: 4 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <Card className="glass-effect">
            <CardContent className="p-12 text-center">
              <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data Available</h3>
              <p className="text-gray-600">Start tracking income and expenses to see financial analytics.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Financial Page
const FinancialPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  });
  const [editingCell, setEditingCell] = useState(null); // {category, date, type}
  const [editValue, setEditValue] = useState("");
  const [transactionType, setTransactionType] = useState("expense"); // "expense" or "income"
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const inputRef = React.useRef(null);

  // Load categories from localStorage on mount
  useEffect(() => {
    const savedCategories = localStorage.getItem('dairypilot_categories');
    if (savedCategories) {
      try {
        setCategories(JSON.parse(savedCategories));
      } catch (e) {
        console.error('Failed to load categories from localStorage');
      }
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
    fetchIncome();
  }, [currentWeekStart]);

  // Merge categories from data whenever expenses or income changes
  useEffect(() => {
    if (expenses.length > 0 || income.length > 0) {
      mergeCategories();
    }
  }, [expenses, income]);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`);
      setExpenses(response.data);
    } catch (error) {
      toast.error("Failed to fetch expenses");
    }
  };

  const fetchIncome = async () => {
    try {
      const response = await axios.get(`${API}/income`);
      setIncome(response.data);
    } catch (error) {
      toast.error("Failed to fetch income");
    }
  };

  const mergeCategories = () => {
    console.log('mergeCategories called');
    // Extract categories from actual data
    const expenseCategoriesFromData = [...new Set(expenses.map(e => e.category))].map(cat => ({
      name: cat,
      type: 'expense'
    }));
    const incomeCategoriesFromData = [...new Set(income.map(i => i.product_name || 'Income'))].map(cat => ({
      name: cat,
      type: 'income'
    }));
    
    console.log('Categories from data:', { expenseCategoriesFromData, incomeCategoriesFromData });
    
    // Load saved categories from localStorage
    const savedCategories = localStorage.getItem('dairypilot_categories');
    let savedCats = [];
    if (savedCategories) {
      try {
        savedCats = JSON.parse(savedCategories);
        console.log('Loaded from localStorage:', savedCats);
      } catch (e) {
        console.error('Failed to parse saved categories');
      }
    }
    
    // Merge all categories
    const allCategories = [...savedCats, ...expenseCategoriesFromData, ...incomeCategoriesFromData];
    console.log('All categories before dedup:', allCategories);
    
    // Remove duplicates
    const uniqueCategories = allCategories.filter((cat, index, self) =>
      index === self.findIndex((c) => c.name === cat.name && c.type === cat.type)
    );
    
    console.log('Unique categories:', uniqueCategories);
    
    // Only update if categories have actually changed
    setCategories(prev => {
      if (JSON.stringify(prev) === JSON.stringify(uniqueCategories)) {
        console.log('Categories unchanged, skipping update');
        return prev;
      }
      console.log('Updating categories from', prev, 'to', uniqueCategories);
      // Persist to localStorage
      localStorage.setItem('dairypilot_categories', JSON.stringify(uniqueCategories));
      return uniqueCategories;
    });
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getTransactionForCell = (category, date, type) => {
    const dateStr = formatDate(date);
    if (type === 'expense') {
      return expenses.find(e => e.category === category && e.date === dateStr);
    } else {
      return income.find(i => (i.product_name || 'Income') === category && i.date === dateStr);
    }
  };

  const handleCellClick = (category, date, type) => {
    const existing = getTransactionForCell(category, date, type);
    setEditingCell({ category, date, type });
    setEditValue(existing ? existing.amount.toString() : "");
  };

  React.useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  const handleSave = async (category, date, type) => {
    if (!editValue || editValue.trim() === "" || isNaN(parseFloat(editValue))) {
      // If empty or invalid, check if we need to delete
      const existing = getTransactionForCell(category, date, type);
      if (existing) {
        await handleDelete(existing, type);
      }
      setEditingCell(null);
      setEditValue("");
      return;
    }

    const dateStr = formatDate(date);
    const existing = getTransactionForCell(category, date, type);
    
    try {
      if (type === 'expense') {
        const data = {
          date: dateStr,
          category: category,
          amount: parseFloat(editValue),
          expense_type: "variable",
          description: "",
          is_recurring: false
        };
        
        if (existing) {
          await axios.put(`${API}/expenses/${existing.id}`, data);
          toast.success("Expense updated");
        } else {
          await axios.post(`${API}/expenses`, data);
          toast.success("Expense added");
        }
        fetchExpenses();
      } else {
        const data = {
          date: dateStr,
          product_id: "",
          product_name: category,
          quantity: 1,
          amount: parseFloat(editValue),
          sale_channel: "direct",
          customer_name: ""
        };
        
        if (existing) {
          await axios.put(`${API}/income/${existing.id}`, data);
          toast.success("Income updated");
        } else {
          await axios.post(`${API}/income`, data);
          toast.success("Income added");
        }
        fetchIncome();
      }
      
      setEditingCell(null);
      setEditValue("");
    } catch (error) {
      toast.error("Failed to save transaction");
    }
  };

  const handleDelete = async (transaction, type) => {
    try {
      if (type === 'expense') {
        await axios.delete(`${API}/expenses/${transaction.id}`);
        toast.success("Expense deleted");
        fetchExpenses();
      } else {
        await axios.delete(`${API}/income/${transaction.id}`);
        toast.success("Income deleted");
        fetchIncome();
      }
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  const handleKeyDown = (e, category, date, type) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave(category, date, type);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue("");
    }
  };

  const addCategory = () => {
    console.log('addCategory called', { newCategory, transactionType, categories });
    if (newCategory.trim()) {
      const type = transactionType;
      if (!categories.some(c => c.name === newCategory.trim() && c.type === type)) {
        const updatedCategories = [...categories, { name: newCategory.trim(), type }];
        console.log('Setting categories to:', updatedCategories);
        setCategories(updatedCategories);
        // Persist to localStorage
        localStorage.setItem('dairypilot_categories', JSON.stringify(updatedCategories));
        console.log('Saved to localStorage');
      } else {
        console.log('Category already exists');
      }
      setNewCategory("");
      setShowCategoryDialog(false);
    } else {
      console.log('Category name is empty');
    }
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  const weekDays = getWeekDays();
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const expenseCategories = categories.filter(c => c.type === 'expense');
  const incomeCategories = categories.filter(c => c.type === 'income');

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <nav className="bg-white border-b border-green-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-10 h-10 mr-3 rounded-lg" />
                <span className="text-2xl font-bold text-green-800">DairyPilot</span>
              </Link>
              <Link to="/" className="text-gray-600 hover:text-green-600">Dashboard</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-green-900 mb-2">Weekly Financial Tracking</h1>
          <p className="text-gray-600">Click any cell to directly enter amounts - changes save automatically</p>
        </div>

        {/* Week Navigation */}
        <div className="mb-6 flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
          <Button variant="outline" onClick={goToPreviousWeek} className="border-green-600 text-green-600 hover:bg-green-50">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous Week
          </Button>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-800">
              {weekDays[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <Button variant="link" onClick={goToCurrentWeek} className="text-sm text-green-600">
              Go to Current Week
            </Button>
          </div>
          <Button variant="outline" onClick={goToNextWeek} className="border-green-600 text-green-600 hover:bg-green-50">
            Next Week
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </div>

        {/* Tabs for Expenses and Income */}
        <Tabs defaultValue="expenses" className="space-y-6">
          <TabsList className="bg-white border border-green-100">
            <TabsTrigger value="expenses" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">Expenses</TabsTrigger>
            <TabsTrigger value="income" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">Income</TabsTrigger>
          </TabsList>

          <TabsContent value="expenses" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Expense Categories</h2>
              <Button onClick={() => { setTransactionType('expense'); setShowCategoryDialog(true); }} className="bg-green-600 hover:bg-green-700">
                + Add Category
              </Button>
            </div>

            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-red-50 border-b-2 border-red-200">
                        <th className="p-4 text-left font-semibold text-gray-700 sticky left-0 bg-red-50 z-10 min-w-[150px]">Category</th>
                        {weekDays.map((day, idx) => (
                          <th key={idx} className="p-4 text-center font-semibold text-gray-700 min-w-[120px] border-l border-red-100">
                            <div className="text-sm">{dayNames[idx]}</div>
                            <div className="text-xs text-gray-500 mt-1">{day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {expenseCategories.map((cat, catIdx) => (
                        <tr key={cat.name} className={`border-b hover:bg-red-25 ${catIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <td className="p-4 font-medium text-gray-800 sticky left-0 bg-inherit z-10 border-r border-gray-200">{cat.name}</td>
                          {weekDays.map((day, dayIdx) => {
                            const transaction = getTransactionForCell(cat.name, day, 'expense');
                            const isToday = formatDate(day) === formatDate(new Date());
                            const isEditing = editingCell && editingCell.category === cat.name && formatDate(editingCell.date) === formatDate(day) && editingCell.type === 'expense';
                            return (
                              <td key={dayIdx} className={`p-2 text-center border-l border-gray-100 ${isToday ? 'bg-yellow-50' : ''}`}>
                                {isEditing ? (
                                  <input
                                    ref={inputRef}
                                    type="number"
                                    step="0.01"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleSave(cat.name, day, 'expense')}
                                    onKeyDown={(e) => handleKeyDown(e, cat.name, day, 'expense')}
                                    className="w-full px-2 py-1 text-center text-lg font-bold text-red-700 border-2 border-red-500 rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                                    placeholder="0"
                                  />
                                ) : (
                                  <div onClick={() => handleCellClick(cat.name, day, 'expense')} className="cursor-pointer hover:bg-red-100 transition-colors p-2 rounded min-h-[40px] flex items-center justify-center">
                                    {transaction ? <span className="text-lg font-bold text-red-700">₹{transaction.amount.toLocaleString()}</span> : <span className="text-gray-300 text-2xl">-</span>}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {expenseCategories.length === 0 && (
                        <tr><td colSpan={8} className="p-8 text-center text-gray-500">No expense categories. Add categories to track expenses.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="income" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Income Categories</h2>
              <Button onClick={() => { setTransactionType('income'); setShowCategoryDialog(true); }} className="bg-green-600 hover:bg-green-700">
                + Add Category
              </Button>
            </div>

            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-green-50 border-b-2 border-green-200">
                        <th className="p-4 text-left font-semibold text-gray-700 sticky left-0 bg-green-50 z-10 min-w-[150px]">Category</th>
                        {weekDays.map((day, idx) => (
                          <th key={idx} className="p-4 text-center font-semibold text-gray-700 min-w-[120px] border-l border-green-100">
                            <div className="text-sm">{dayNames[idx]}</div>
                            <div className="text-xs text-gray-500 mt-1">{day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {incomeCategories.map((cat, catIdx) => (
                        <tr key={cat.name} className={`border-b hover:bg-green-25 ${catIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <td className="p-4 font-medium text-gray-800 sticky left-0 bg-inherit z-10 border-r border-gray-200">{cat.name}</td>
                          {weekDays.map((day, dayIdx) => {
                            const transaction = getTransactionForCell(cat.name, day, 'income');
                            const isToday = formatDate(day) === formatDate(new Date());
                            const isEditing = editingCell && editingCell.category === cat.name && formatDate(editingCell.date) === formatDate(day) && editingCell.type === 'income';
                            return (
                              <td key={dayIdx} className={`p-2 text-center border-l border-gray-100 ${isToday ? 'bg-yellow-50' : ''}`}>
                                {isEditing ? (
                                  <input
                                    ref={inputRef}
                                    type="number"
                                    step="0.01"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleSave(cat.name, day, 'income')}
                                    onKeyDown={(e) => handleKeyDown(e, cat.name, day, 'income')}
                                    className="w-full px-2 py-1 text-center text-lg font-bold text-green-700 border-2 border-green-500 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                                    placeholder="0"
                                  />
                                ) : (
                                  <div onClick={() => handleCellClick(cat.name, day, 'income')} className="cursor-pointer hover:bg-green-100 transition-colors p-2 rounded min-h-[40px] flex items-center justify-center">
                                    {transaction ? <span className="text-lg font-bold text-green-700">₹{transaction.amount.toLocaleString()}</span> : <span className="text-gray-300 text-2xl">-</span>}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {incomeCategories.length === 0 && (
                        <tr><td colSpan={8} className="p-8 text-center text-gray-500">No income categories. Add categories to track income.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Category Dialog */}
        <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
          <DialogContent className="max-w-md z-[100]">
            <DialogHeader>
              <DialogTitle>Add New {transactionType === 'expense' ? 'Expense' : 'Income'} Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Category Name</Label>
                <Input placeholder="e.g., Feed, Medicine, Milk Sales" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addCategory()} autoFocus />
              </div>
              <Button type="button" onClick={addCategory} className="w-full bg-green-600 hover:bg-green-700 z-[101]">Add Category</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Continue in next file due to length...

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/overview" element={<ProtectedRoute><OverviewPage /></ProtectedRoute>} />
            <Route path="/tracking" element={<ProtectedRoute><TrackingPage /></ProtectedRoute>} />
            <Route path="/production-analytics" element={<ProtectedRoute><ProductionAnalyticsPage /></ProtectedRoute>} />
            <Route path="/income-analytics" element={<ProtectedRoute><IncomeAnalyticsPage /></ProtectedRoute>} />
            <Route path="/expense-analytics" element={<ProtectedRoute><ExpenseAnalyticsPage /></ProtectedRoute>} />
            <Route path="/profit-analytics" element={<ProtectedRoute><ProfitAnalyticsPage /></ProtectedRoute>} />
            <Route path="/financial-analytics" element={<ProtectedRoute><FinancialAnalyticsPage /></ProtectedRoute>} />
            <Route path="/financial" element={<ProtectedRoute><FinancialPage /></ProtectedRoute>} />
            <Route path="/production" element={<ProtectedRoute><ProductionPage /></ProtectedRoute>} />
            <Route path="/animals" element={<ProtectedRoute><AnimalsPage /></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
        <div className="text-2xl text-green-700">Loading...</div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/auth" />;
};

// Production Page Component
const ProductionPage = () => {
  const [production, setProduction] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    return new Date(today.setDate(diff));
  });
  const [editingCell, setEditingCell] = useState(null); // {productId, date}
  const [editValue, setEditValue] = useState("");
  const inputRef = React.useRef(null);
  const { formatVolume, formatWeight, getVolumeUnit, getWeightUnit } = useSettings();

  useEffect(() => {
    fetchProduction();
    fetchProducts();
  }, [currentWeekStart]);

  const fetchProduction = async () => {
    try {
      const response = await axios.get(`${API}/production`);
      setProduction(response.data);
    } catch (error) {
      toast.error("Failed to fetch production");
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    }
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getProductionForCell = (productId, date) => {
    const dateStr = formatDate(date);
    return production.find(p => p.product_id === productId && p.date === dateStr);
  };

  const handleCellClick = (product, date) => {
    const existing = getProductionForCell(product.id, date);
    setEditingCell({ productId: product.id, date, product });
    setEditValue(existing ? existing.quantity.toString() : "");
  };

  React.useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  const handleSave = async (product, date) => {
    if (!editValue || editValue.trim() === "" || isNaN(parseFloat(editValue))) {
      // If empty or invalid, check if we need to delete
      const existing = getProductionForCell(product.id, date);
      if (existing) {
        await handleDelete(existing);
      }
      setEditingCell(null);
      setEditValue("");
      return;
    }

    const dateStr = formatDate(date);
    const existing = getProductionForCell(product.id, date);
    const data = {
      date: dateStr,
      product_id: product.id,
      product_name: product.name,
      quantity: parseFloat(editValue)
    };

    try {
      if (existing) {
        await axios.put(`${API}/production/${existing.id}`, data);
        toast.success("Production updated");
      } else {
        await axios.post(`${API}/production`, data);
        toast.success("Production added");
      }
      setEditingCell(null);
      setEditValue("");
      fetchProduction();
    } catch (error) {
      toast.error("Failed to save production");
    }
  };

  const handleDelete = async (productionRecord) => {
    try {
      await axios.delete(`${API}/production/${productionRecord.id}`);
      toast.success("Production deleted");
      fetchProduction();
    } catch (error) {
      toast.error("Failed to delete production");
    }
  };

  const handleKeyDown = (e, product, date) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave(product, date);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue("");
    }
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  const weekDays = getWeekDays();
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <nav className="bg-white border-b border-green-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-10 h-10 mr-3 rounded-lg" />
                <span className="text-2xl font-bold text-green-800">DairyPilot</span>
              </Link>
              <Link to="/" className="text-gray-600 hover:text-green-600">Dashboard</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-green-900 mb-2">Weekly Production Tracking</h1>
          <p className="text-gray-600">Click any cell to directly enter quantities - changes save automatically</p>
        </div>

        {/* Week Navigation */}
        <div className="mb-6 flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
          <Button variant="outline" onClick={goToPreviousWeek} className="border-blue-600 text-blue-600 hover:bg-blue-50">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous Week
          </Button>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-800">
              {weekDays[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <Button variant="link" onClick={goToCurrentWeek} className="text-sm text-blue-600">
              Go to Current Week
            </Button>
          </div>
          <Button variant="outline" onClick={goToNextWeek} className="border-blue-600 text-blue-600 hover:bg-blue-50">
            Next Week
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </div>

        {/* Weekly Production Grid */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-blue-50 border-b-2 border-blue-200">
                    <th className="p-4 text-left font-semibold text-gray-700 sticky left-0 bg-blue-50 z-10 min-w-[150px]">
                      Product
                    </th>
                    {weekDays.map((day, idx) => (
                      <th key={idx} className="p-4 text-center font-semibold text-gray-700 min-w-[120px] border-l border-blue-100">
                        <div className="text-sm">{dayNames[idx]}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, prodIdx) => (
                    <tr key={product.id} className={`border-b hover:bg-blue-25 ${prodIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="p-4 font-medium text-gray-800 sticky left-0 bg-inherit z-10 border-r border-gray-200">
                        <div>{product.name}</div>
                        <div className="text-xs text-gray-500">{product.unit}</div>
                      </td>
                      {weekDays.map((day, dayIdx) => {
                        const cellProduction = getProductionForCell(product.id, day);
                        const isToday = formatDate(day) === formatDate(new Date());
                        const isEditing = editingCell && editingCell.productId === product.id && formatDate(editingCell.date) === formatDate(day);
                        return (
                          <td 
                            key={dayIdx} 
                            className={`p-2 text-center border-l border-gray-100 ${isToday ? 'bg-yellow-50' : ''}`}
                            data-testid={`cell-${product.id}-${dayIdx}`}
                          >
                            {isEditing ? (
                              <input
                                ref={inputRef}
                                type="number"
                                step="0.01"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => handleSave(product, day)}
                                onKeyDown={(e) => handleKeyDown(e, product, day)}
                                className="w-full px-2 py-1 text-center text-lg font-bold text-blue-700 border-2 border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                                placeholder="0"
                              />
                            ) : (
                              <div onClick={() => handleCellClick(product, day)} className="cursor-pointer hover:bg-blue-100 transition-colors p-2 rounded min-h-[40px] flex items-center justify-center">
                                {cellProduction ? (
                                  <span className="text-lg font-bold text-blue-700">{cellProduction.quantity}</span>
                                ) : (
                                  <span className="text-gray-300 text-2xl">-</span>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {products.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-gray-500">
                        No products found. Add products first to track production.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Animals Page Component
const AnimalsPage = () => {
  const [animals, setAnimals] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showAnimalDialog, setShowAnimalDialog] = useState(false);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [animalForm, setAnimalForm] = useState({
    name: "",
    animal_type: "cow",
    breed: "",
    dob: "",
    lactation_month: 0,
    is_pregnant: false,
    pregnancy_month: 0,
    batch_id: "none"
  });
  const [batchForm, setBatchForm] = useState({
    name: "",
    description: ""
  });

  useEffect(() => {
    fetchAnimals();
    fetchBatches();
  }, []);

  const fetchAnimals = async () => {
    try {
      const response = await axios.get(`${API}/animals`);
      setAnimals(response.data);
    } catch (error) {
      toast.error("Failed to fetch animals");
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${API}/batches`);
      setBatches(response.data);
    } catch (error) {
      toast.error("Failed to fetch batches");
    }
  };

  const handleAnimalSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...animalForm,
        lactation_month: parseInt(animalForm.lactation_month),
        pregnancy_month: parseInt(animalForm.pregnancy_month),
        batch_id: animalForm.batch_id === "none" ? "" : animalForm.batch_id
      };
      
      if (editingAnimal) {
        await axios.put(`${API}/animals/${editingAnimal.id}`, data);
        toast.success("Animal updated successfully");
      } else {
        await axios.post(`${API}/animals`, data);
        toast.success("Animal added successfully");
      }
      setShowAnimalDialog(false);
      setEditingAnimal(null);
      setAnimalForm({
        name: "",
        animal_type: "cow",
        breed: "",
        dob: "",
        lactation_month: 0,
        is_pregnant: false,
        pregnancy_month: 0,
        batch_id: "none"
      });
      fetchAnimals();
    } catch (error) {
      toast.error(editingAnimal ? "Failed to update animal" : "Failed to add animal");
    }
  };

  const editAnimal = (animal) => {
    setEditingAnimal(animal);
    setAnimalForm({
      name: animal.name,
      animal_type: animal.animal_type,
      breed: animal.breed || "",
      dob: animal.dob || "",
      lactation_month: animal.lactation_month,
      is_pregnant: animal.is_pregnant,
      pregnancy_month: animal.pregnancy_month,
      batch_id: animal.batch_id || "none"
    });
    setShowAnimalDialog(true);
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/batches`, batchForm);
      toast.success("Batch created successfully");
      setShowBatchDialog(false);
      setBatchForm({ name: "", description: "" });
      fetchBatches();
    } catch (error) {
      toast.error("Failed to create batch");
    }
  };

  const deleteAnimal = async (id) => {
    try {
      await axios.delete(`${API}/animals/${id}`);
      toast.success("Animal deleted");
      fetchAnimals();
    } catch (error) {
      toast.error("Failed to delete animal");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <nav className="bg-white border-b border-green-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-10 h-10 mr-3 rounded-lg" />
                <span className="text-2xl font-bold text-green-800">DairyPilot</span>
              </Link>
              <Link to="/" className="text-gray-600 hover:text-green-600">Dashboard</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-green-900 mb-2">Animal Management</h1>
            <p className="text-gray-600">Manage your livestock</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50" data-testid="add-batch-button">
                  Add Batch
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Batch</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleBatchSubmit} className="space-y-4">
                  <div>
                    <Label>Batch Name</Label>
                    <Input placeholder="e.g., Summer 2024" value={batchForm.name} onChange={(e) => setBatchForm({...batchForm, name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input placeholder="Optional" value={batchForm.description} onChange={(e) => setBatchForm({...batchForm, description: e.target.value})} />
                  </div>
                  <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">Create Batch</Button>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={showAnimalDialog} onOpenChange={setShowAnimalDialog}>
              <DialogTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-700" data-testid="add-animal-button">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Animal
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingAnimal ? 'Edit Animal' : 'Add New Animal'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAnimalSubmit} className="space-y-4">
                  <div>
                    <Label>Name/ID</Label>
                    <Input placeholder="Animal name or ID" value={animalForm.name} onChange={(e) => setAnimalForm({...animalForm, name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={animalForm.animal_type} onValueChange={(value) => setAnimalForm({...animalForm, animal_type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cow">Cow</SelectItem>
                        <SelectItem value="buffalo">Buffalo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Breed</Label>
                    <Input placeholder="Optional" value={animalForm.breed} onChange={(e) => setAnimalForm({...animalForm, breed: e.target.value})} />
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input type="date" value={animalForm.dob} onChange={(e) => setAnimalForm({...animalForm, dob: e.target.value})} />
                  </div>
                  <div>
                    <Label>Lactation Month</Label>
                    <Input type="number" min="0" value={animalForm.lactation_month} onChange={(e) => setAnimalForm({...animalForm, lactation_month: e.target.value})} />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="is_pregnant" checked={animalForm.is_pregnant} onChange={(e) => setAnimalForm({...animalForm, is_pregnant: e.target.checked})} />
                    <Label htmlFor="is_pregnant" className="cursor-pointer">Pregnant</Label>
                  </div>
                  {animalForm.is_pregnant && (
                    <div>
                      <Label>Pregnancy Month</Label>
                      <Input type="number" min="0" max="9" value={animalForm.pregnancy_month} onChange={(e) => setAnimalForm({...animalForm, pregnancy_month: e.target.value})} />
                    </div>
                  )}
                  <div>
                    <Label>Batch</Label>
                    <Select value={animalForm.batch_id} onValueChange={(value) => setAnimalForm({...animalForm, batch_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select batch (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Batch</SelectItem>
                        {batches.map((batch) => (
                          <SelectItem key={batch.id} value={batch.id}>{batch.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">{editingAnimal ? 'Update Animal' : 'Add Animal'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {batches.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-3">Batches</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {batches.map((batch) => (
                <Card key={batch.id} className="bg-white border-amber-100">
                  <CardHeader>
                    <CardTitle className="text-lg">{batch.name}</CardTitle>
                    <CardDescription>{batch.description || 'No description'}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-gray-600">Animals: {batch.animal_count}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name/ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Breed</TableHead>
                    <TableHead>DOB</TableHead>
                    <TableHead>Lactation</TableHead>
                    <TableHead>Pregnancy</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {animals.map((animal) => (
                    <TableRow key={animal.id}>
                      <TableCell className="font-semibold">{animal.name}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${animal.animal_type === 'cow' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {animal.animal_type}
                        </span>
                      </TableCell>
                      <TableCell>{animal.breed || '-'}</TableCell>
                      <TableCell>{animal.dob || '-'}</TableCell>
                      <TableCell>{animal.lactation_month} months</TableCell>
                      <TableCell>
                        {animal.is_pregnant ? (
                          <span className="text-green-700 font-medium">{animal.pregnancy_month} months</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => editAnimal(animal)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteAnimal(animal.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Products Page Component
const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const { formatCurrency, metricSystem, getVolumeUnit, getWeightUnit } = useSettings();
  const [form, setForm] = useState({
    name: "",
    unit: metricSystem === 'imperial' ? 'gallons' : 'liters',
    default_price: ""
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  // Reset form unit when metric system changes
  useEffect(() => {
    if (!editingProduct) {
      setForm(prev => ({
        ...prev,
        unit: metricSystem === 'imperial' ? 'gallons' : 'liters'
      }));
    }
  }, [metricSystem, editingProduct]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        default_price: parseFloat(form.default_price)
      };
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, data);
        toast.success("Product added successfully");
      }
      setShowDialog(false);
      setEditingProduct(null);
      setForm({ name: "", unit: metricSystem === 'imperial' ? 'gallons' : 'liters', default_price: "" });
      fetchProducts();
    } catch (error) {
      toast.error(editingProduct ? "Failed to update product" : "Failed to add product");
    }
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      unit: product.unit,
      default_price: product.default_price.toString()
    });
    setShowDialog(true);
  };

  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <nav className="bg-white border-b border-green-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center">
                <img src="/logo.png?v=2" alt="DairyPilot Logo" className="w-10 h-10 mr-3 rounded-lg" />
                <span className="text-2xl font-bold text-green-800">DairyPilot</span>
              </Link>
              <Link to="/" className="text-gray-600 hover:text-green-600">Dashboard</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-green-900 mb-2">Product Catalog</h1>
            <p className="text-gray-600">Manage your dairy products</p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700" data-testid="add-product-button">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Product Name</Label>
                  <Input placeholder="e.g., Milk, Cheese, Butter" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select value={form.unit} onValueChange={(value) => setForm({...form, unit: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {metricSystem === 'imperial' ? (
                        <>
                          <SelectItem value="gallons">Gallons</SelectItem>
                          <SelectItem value="lbs">Pounds (Lbs)</SelectItem>
                          <SelectItem value="pieces">Pieces</SelectItem>
                          <SelectItem value="oz">Ounces (Oz)</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="liters">Liters</SelectItem>
                          <SelectItem value="kg">Kilograms (Kg)</SelectItem>
                          <SelectItem value="pieces">Pieces</SelectItem>
                          <SelectItem value="grams">Grams</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Default Price ({useSettings().getCurrencySymbol()})</Label>
                  <Input type="number" step="0.01" placeholder="Price per unit" value={form.default_price} onChange={(e) => setForm({...form, default_price: e.target.value})} required />
                </div>
                <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">{editingProduct ? 'Update Product' : 'Add Product'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <Card key={product.id} className="bg-white border-purple-100 hover:shadow-lg transition-all">
              <CardHeader>
                <CardTitle className="text-xl text-purple-800">{product.name}</CardTitle>
                <CardDescription>Unit: {product.unit}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div className="text-2xl font-bold text-green-700">{formatCurrency(product.default_price)}</div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => editProduct(product)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteProduct(product.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};